import { useState, type FormEvent } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  EmailLinkAccount,
  EmailLinkErrorKey,
  EmailLinkPhase,
} from "@/hooks/use-email-link-auth";
import { StepHeading } from "../OnboardingShell";
import { LinkButton, PrimaryButton, TextButton } from "../ui/buttons";
import { TextField } from "../ui/TextField";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Swedish mobile numbers: 07x or +467x followed by eight digits, separators ignored. */
const MOBILE_RE = /^(\+46|0)7\d{8}$/;

export function validateAccount(values: EmailLinkAccount): Partial<Record<keyof EmailLinkAccount, true>> {
  const errors: Partial<Record<keyof EmailLinkAccount, true>> = {};
  if (!values.name.trim()) errors.name = true;
  if (!EMAIL_RE.test(values.email.trim())) errors.email = true;
  if (!MOBILE_RE.test(values.mobile.replace(/[\s-]/g, ""))) errors.mobile = true;
  return errors;
}

interface AccountStepProps {
  phase: EmailLinkPhase;
  email: string;
  error: EmailLinkErrorKey | null;
  resendAvailableIn: number;
  initial: Partial<EmailLinkAccount>;
  onSend: (values: EmailLinkAccount) => void;
  onResend: () => void;
  onConfirmEmail: (
    email: string,
    fallbackAccount?: Pick<EmailLinkAccount, "name" | "mobile">,
  ) => void;
  onBackToForm: () => void;
  /** Already signed in: offer to continue as that person instead of a form. */
  signedInAs?: { name: string; email: string } | null;
  onContinue: () => void;
  onSignOut: () => void;
}

/**
 * Step 1: name, email and mobile, then a one-time sign-in link by email.
 * The copy is sector-neutral on purpose: the sector is chosen afterwards.
 */
export function AccountStep({
  phase,
  email,
  error,
  resendAvailableIn,
  initial,
  onSend,
  onResend,
  onConfirmEmail,
  onBackToForm,
  signedInAs,
  onContinue,
  onSignOut,
}: AccountStepProps) {
  const { t } = useTranslation("onboarding");
  const [values, setValues] = useState<EmailLinkAccount>({
    name: initial.name ?? "",
    email: initial.email ?? "",
    mobile: initial.mobile ?? "",
  });
  const [touched, setTouched] = useState<Partial<Record<keyof EmailLinkAccount, boolean>>>({});
  const [confirmValue, setConfirmValue] = useState("");

  if (signedInAs) {
    return (
      <div data-testid="onboarding-account-signed-in">
        <StepHeading
          title={t("account.signedInAs", { name: signedInAs.name || signedInAs.email })}
          lead={t("account.signedInBody")}
        />
        <div className="flex flex-wrap items-center gap-3">
          <PrimaryButton onClick={onContinue}>{t("account.continue")}</PrimaryButton>
          <TextButton onClick={onSignOut}>{t("account.signOut")}</TextButton>
        </div>
      </div>
    );
  }

  const errors = validateAccount(values);
  const field = (key: keyof EmailLinkAccount) => ({
    value: values[key],
    onChange: (e: FormEvent<HTMLInputElement>) => {
      // Read before the updater runs: React clears currentTarget after the handler.
      const value = e.currentTarget.value;
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    onBlur: () => setTouched((prev) => ({ ...prev, [key]: true })),
    error: touched[key] && errors[key] ? t(`account.errors.${key}`) : null,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, mobile: true });
    if (Object.keys(errors).length > 0) return;
    onSend({ ...values, name: values.name.trim(), email: values.email.trim() });
  };

  if (phase === "verifying") {
    return (
      <div className="flex items-center gap-3 text-[15px] text-g-ink-600" role="status">
        <Loader2 className="h-5 w-5 animate-spin text-g-primary" aria-hidden="true" />
        {t("account.verifying")}
      </div>
    );
  }

  if (phase === "confirmEmail") {
    // A link opened on another device has no local draft, so this screen
    // also collects name and mobile, not just the email: otherwise an
    // owner's name and phone are silently dropped from the account.
    const confirmMobileDigits = values.mobile.replace(/[\s-]/g, "");
    const canSubmitConfirm =
      EMAIL_RE.test(confirmValue.trim()) &&
      !!values.name.trim() &&
      MOBILE_RE.test(confirmMobileDigits);
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setTouched((prev) => ({ ...prev, name: true, mobile: true }));
          if (!canSubmitConfirm) return;
          onConfirmEmail(confirmValue.trim(), {
            name: values.name.trim(),
            mobile: values.mobile.trim(),
          });
        }}
        data-testid="onboarding-account-confirm"
      >
        <StepHeading title={t("account.confirmEmail.title")} lead={t("account.confirmEmail.body")} />
        <div className="flex flex-col gap-4">
          <TextField
            id="onb-confirm-email"
            label={t("account.fields.email")}
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder={t("account.fields.emailPlaceholder")}
            value={confirmValue}
            onChange={(e) => setConfirmValue(e.currentTarget.value)}
            error={error ? t(`account.errors.${error}`) : null}
          />
          <TextField
            id="onb-confirm-name"
            label={t("account.fields.name")}
            autoComplete="name"
            placeholder={t("account.fields.namePlaceholder")}
            {...field("name")}
          />
          <TextField
            id="onb-confirm-mobile"
            label={t("account.fields.mobile")}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={t("account.fields.mobilePlaceholder")}
            {...field("mobile")}
          />
        </div>
        <div className="mt-6">
          <PrimaryButton type="submit" disabled={!canSubmitConfirm}>
            {t("account.confirmEmail.cta")}
          </PrimaryButton>
        </div>
      </form>
    );
  }

  if (phase === "error") {
    return (
      <div data-testid="onboarding-account-error">
        <StepHeading title={t("account.title")} />
        <p className="rounded-g-md border border-g-danger bg-g-danger-tint px-4 py-3 text-[14px] font-medium text-g-danger" role="alert">
          {t(`account.errors.${error ?? "signInFailed"}`)}
        </p>
        <div className="mt-6">
          <PrimaryButton onClick={onBackToForm}>{t("account.errors.sendNew")}</PrimaryButton>
        </div>
      </div>
    );
  }

  if (phase === "sent") {
    return (
      <div data-testid="onboarding-account-sent">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-g-pill bg-g-success-tint text-g-success" aria-hidden="true">
          <MailCheck className="h-6 w-6" />
        </div>
        <StepHeading title={t("account.sent.title")} lead={t("account.sent.body", { email })} />
        <div className="flex flex-wrap items-center gap-4">
          <PrimaryButton onClick={onResend} disabled={resendAvailableIn > 0}>
            {resendAvailableIn > 0
              ? t("account.resendIn", { seconds: resendAvailableIn })
              : t("account.resend")}
          </PrimaryButton>
          <LinkButton onClick={onBackToForm}>{t("account.changeEmail")}</LinkButton>
        </div>
        {error ? (
          <p className="mt-4 text-[13px] font-medium text-g-danger" role="alert">
            {t(`account.errors.${error}`)}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="onboarding-account-form">
      <StepHeading title={t("account.title")} lead={t("account.lead")} />
      <div className="flex flex-col gap-4">
        <TextField
          id="onb-name"
          label={t("account.fields.name")}
          autoComplete="name"
          placeholder={t("account.fields.namePlaceholder")}
          {...field("name")}
        />
        <TextField
          id="onb-email"
          label={t("account.fields.email")}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t("account.fields.emailPlaceholder")}
          {...field("email")}
        />
        <TextField
          id="onb-mobile"
          label={t("account.fields.mobile")}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={t("account.fields.mobilePlaceholder")}
          {...field("mobile")}
        />
      </div>
      {error ? (
        <p className="mt-4 text-[13px] font-medium text-g-danger" role="alert">
          {t(`account.errors.${error}`)}
        </p>
      ) : null}
      <div className="mt-8 flex justify-end">
        <PrimaryButton type="submit">{t("account.send")}</PrimaryButton>
      </div>
    </form>
  );
}
